/**
 * compositions — the OFFICIAL L3 Domain compositions, as DATA.
 *
 * An L3 Domain component is not native code: it is a `composition` node tree
 * built from L1/L2 types, bound to a `props.*` parameter interface (README
 * §6.2). That is what makes it HOT-UPDATABLE — it ships in a widget's
 * `view.json` bundle or in `registry.json`, and needs no app release.
 *
 * This file is the web runtime's BUNDLED copy of the official ones, exactly
 * the way `registry.js` mirrors the server registry's implemented set: the
 * contract lives in `backend/widget-native/registry.json`, a test diffs the
 * two, and `registerRegistry()` overlays the SERVER's definitions at runtime
 * (which is the hot-update path made real — a composition changed on the
 * server renders differently with no new bundle).
 *
 * Shape (mirrors the registry entry):
 *   introduced_in_app  the app build whose kit this composition needs (its
 *                      expander + the leaves it references). An older app
 *                      renders `fallback` instead — §4.5 degradation.
 *   dataProps          which props are context PATHS (evaluated in the caller's
 *                      scope before the body sees them as `props.*`)
 *   nodeProps          which props are node sub-trees, passed verbatim
 *   signals            the composition's OUTWARD signals + the inner signal
 *                      each one maps from (`from`), default = pass-through
 *   slots              outer slot → `"<innerKey>.<slot>"` forwarding
 *   fallback           §4.5: what an app without this composition renders
 *   composition        the node tree; may reference ONLY L1/L2/L3 types, never
 *                      L4 and never itself (cycle-guarded, depth ≤ 3)
 */

/**
 * CaloryRing — the proof case: a domain component that used to be 60 lines of
 * native SVG is now a Gauge + an Overlay + four Texts and two Badges.
 *
 * It reads the `todayTotals` derive's PREFORMATTED fields (`calories_label`,
 * `range_label`, `remaining_label`) off its own `bind` (`data.*`) rather than
 * formatting numbers itself: the derive is the one formatter, so the ring's
 * numeral and the page's copy can never disagree. Domain vocabulary is allowed
 * here and nowhere below — that is the whole definition of L3.
 */
const CaloryRing = {
  id: 'CaloryRing',
  introduced_in_app: '0.9.0',
  dataProps: ['value', 'target'],
  nodeProps: [],
  signals: [],
  slots: [{ name: 'setValue', payload: '{value}', forwardsTo: 'ring.setValue' }],
  // An app that predates the expander still shows the day against the target:
  // function preserved (value out of max), form degraded (a bar, not a ring).
  fallback: {
    type: 'Gauge',
    props: { value: { $: 'props.value' }, max: { $: 'props.target' }, tone: 'accent' },
  },
  composition: {
    type: 'Vertical',
    props: { gap: '2', align: 'center' },
    children: [
      {
        type: 'Overlay',
        children: [
          {
            key: 'ring',
            type: 'Gauge',
            variant: 'ring',
            props: {
              variant: 'ring',
              value: { $: 'props.value' },
              max: { $: 'props.target' },
              size: 168,
              stroke: 14,
              tone: 'accent',
              // over the day's target the ring — not the blueprint — flips tone
              thresholds: [{ at: { $: 'props.target' }, tone: 'warn' }],
            },
          },
          {
            region: 'center',
            type: 'Vertical',
            props: { gap: '1', align: 'center' },
            children: [
              { type: 'Text', variant: 'display', props: { value: '{data.calories_label}', align: 'center' } },
              {
                type: 'Text',
                variant: 'eyebrow',
                when: { path: 'props.target', op: 'nonempty' },
                props: { value: 'of {data.target_label} {props.unit}', tone: 'text-3', align: 'center' },
              },
              {
                type: 'Text',
                variant: 'eyebrow',
                when: { path: 'props.target', op: 'empty' },
                props: { value: '{props.unit}', tone: 'text-3', align: 'center' },
              },
            ],
          },
        ],
      },
      {
        type: 'Horizontal',
        props: { gap: '2', align: 'center', wrap: true },
        children: [
          // the honest low–high band, only when the day actually carries one
          {
            type: 'Text',
            variant: 'caption',
            when: 'data.range_label',
            props: { value: 'honest range {data.range_label} {props.unit}', tone: 'text-3' },
          },
          // …and the remaining chip, tone flipped by the SIGN of what is left
          {
            type: 'Badge',
            when: { path: 'data.remaining', op: 'lt', value: 0 },
            props: { tone: 'warn', text: '{data.remaining_label}' },
          },
          {
            type: 'Badge',
            when: { path: 'data.remaining', op: 'gte', value: 0 },
            props: { tone: 'accent', text: '{data.remaining_label}' },
          },
        ],
      },
    ],
  },
};

/**
 * WeekReview — the whole 'THIS WEEK' card as one domain composition: a BarChart
 * whose bars now carry PER-COLUMN meal thumbnails (each day's meals stacked
 * under its bar, circular, newest-first), a dashed Divider, and a StatTileRow of
 * the two week readouts. It reads a `weekBars`-shaped object off its own `bind`
 * (`data.bars` — each bar's `thumbs[]` included — · `data.avg_label` …) and
 * forwards three signals — `pageChanged {delta,date}` (the chart's paging),
 * `barSelected {date}` (a bar tap) and `thumbActivated` (a thumbnail tap,
 * carrying the tapped row's scope so the page opens the exact log card). The two
 * readout labels are `nodeProps` (`avgLabel` / `loggedLabel`) so another widget
 * can reuse the card with its own words; absent, they default to the meal copy.
 * The Flutter twin is a native builder (`compositions.dart`
 * `buildViewIrWeekReview`) — same view, hand-written, because the Flutter kit
 * has no generic composition expander (as CaloryRing is).
 */
const WeekReview = {
  id: 'WeekReview',
  introduced_in_app: '0.10.0',
  dataProps: ['selected'],
  nodeProps: ['avgLabel', 'loggedLabel'],
  signals: [
    { name: 'pageChanged', payload: '{delta,date}', from: 'pageChanged', note: 'the inner BarChart\'s paging, forwarded verbatim.' },
    { name: 'barSelected', payload: '{date}', from: 'barSelected', note: 'a week-bar tap; wire it to `page_args.<date>` so the highlight and the derived selectors follow the tap.' },
    { name: 'thumbActivated', payload: '{item_id}', from: 'thumbActivated', note: 'a per-column thumbnail tap on the chart; the tapped row\'s scope rides along, so the wire binds `item.item_id` / `item.session_id` / `item.log_card_id`.' },
  ],
  slots: [],
  // An app that predates the composition still shows the week's chart (no thumbs):
  // function preserved, form degraded. `selected` is the resolved day (a dataProp),
  // so even the fallback's highlight follows a bar tap, not the window anchor.
  fallback: {
    type: 'BarChart',
    props: {
      bars: { $: 'data.bars' }, target: { $: 'data.target' },
      selected: { $: 'props.selected' }, title: 'THIS WEEK', maxDate: { $: 'data.window_end' },
    },
  },
  composition: {
    type: 'Surface',
    variant: 'card',
    props: { variant: 'card', padding: '4' },
    children: [
      {
        type: 'Vertical',
        props: { gap: '3' },
        children: [
          {
            key: 'chart',
            type: 'BarChart',
            props: {
              surface: false, title: 'THIS WEEK', pageLabel: 'week',
              bars: { $: 'data.bars' }, target: { $: 'data.target' },
              selected: { $: 'props.selected' }, maxDate: { $: 'data.window_end' },
            },
          },
          { type: 'Divider', props: { variant: 'dashed' } },
          {
            type: 'StatTileRow',
            variant: 'metric',
            bind: 'data',
            props: {
              // The tile KEYS stay the derive's data keys; only the human labels
              // become props, defaulting to the meal copy when the blueprint
              // passes none (back-compat — the currently-pinned blueprint sets
              // no labels and MUST still read the same words until it re-pins).
              tiles: [
                { key: 'avg_label', label: { $: 'props.avgLabel', fallback: 'avg cal / logged day' } },
                { key: 'logged_label', label: { $: 'props.loggedLabel', fallback: 'days logged' } },
              ],
            },
          },
        ],
      },
    ],
  },
};

/** id → composed definition. The official set the web runtime ships with. */
export const COMPOSITIONS = {
  CaloryRing,
  WeekReview,
};

export default COMPOSITIONS;
